import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

/**
 * Service to observe changes in window size
 * screen size changes trigger currentScreenSize update
 * @class BreakpointService
 * @typedef {BreakpointService}
 * @implements {OnDestroy}
 */
@Injectable({
    providedIn: 'root'
})
export class BreakpointService implements OnDestroy {
    /**  lifecycle hook */
    readonly #destroyed: Subject<void> = new Subject<void>();
    readonly #breakpointObserver = inject(BreakpointObserver);

    /** currentScreenSize signal */
    currentScreenSize = signal('unknown');

    /** names of observed breakpoints */
    breakpointNames = new Map([
        [Breakpoints.XSmall, 'XSmall'],
        [Breakpoints.Small, 'Small'],
        [Breakpoints.Medium, 'Medium'],
        [Breakpoints.Large, 'Large'],
        [Breakpoints.XLarge, 'XLarge']
    ]);

    constructor() {
        this.getCurrentScreenSize();
    }

    /**
     * helper function, to observe breakpoint changes
     * sets currentScreenSize to evaluated breakpoint name
     */
    getCurrentScreenSize() {
        this.#breakpointObserver
            .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
            .pipe(takeUntil(this.#destroyed))
            .subscribe((result) => {
                for (const query of Object.keys(result.breakpoints)) {
                    if (result.breakpoints[query]) {
                        const breakpoint = this.breakpointNames.get(query) ?? 'Unknown';
                        this.currentScreenSize.set(breakpoint);
                    }
                }
            });
    }
    /** lifecycle hook */
    ngOnDestroy() {
        this.#destroyed.next();
        this.#destroyed.complete();
    }
}
